// Public contact details. ONE source for the /contact page, the footer and
// Organization schema (contactPoint / address / telephone), so a number shown
// to a human can never disagree with the number handed to Google.
//
// HONESTY RULE: every field here must be real and answered. An unanswered phone
// or a made-up "headquarters" is worse than no phone at all — for a product
// whose whole pitch is verified-not-guessed data, a fabricated NAP is the one
// lie a prospect can actually catch. Empty string / undefined = the block is
// simply not rendered and the schema key is omitted. Nothing breaks.

/** Support inbox. Already published in the footer and the in-app support modal. */
export const SUPPORT_EMAIL = "support@leadsminer.app";

/**
 * Reply-time promise. Kept identical to the in-app support modal
 * (components/contact.tsx), which has promised this since launch — so the
 * public page and the logged-in modal state the same commitment.
 */
export const RESPONSE_TIME = "within 1 business day";

/**
 * Human support hours, e.g. "Monday–Friday, 9:00–18:00 PT".
 * EMPTY until the team confirms the real staffed window — publishing hours
 * nobody is actually online for generates angry "I messaged at 10am" tickets.
 */
export const SUPPORT_HOURS: string = "";

/**
 * Published support/sales number in E.164-ish display form, e.g. "+1 (408) 555-0142".
 * EMPTY until a real, answered line exists. When set it renders on /contact AND
 * becomes Organization.contactPoint.telephone.
 */
export const PHONE: string = "+1 (925) 694-1780";

/**
 * Registered business address. EMPTY until the team confirms it.
 * When every field is set it renders on /contact AND becomes a schema.org
 * PostalAddress on the Organization — one of the strongest local/entity signals
 * Google accepts, which is exactly why it must not be invented.
 */
export type ContactAddress = {
  street: string;
  city: string;
  region: string;
  /**
   * Optional by design: a ZIP nobody confirmed is a guess, and a wrong one on a
   * registered address is worse than an absent one. `hasAddress()` has never
   * required it, and both the page and the schema omit the field when blank.
   */
  postalCode?: string;
  country: string; // ISO 3166-1 alpha-2
};

// `as` rather than a plain annotation: for a `const` initialised to null, TS
// narrows the declared union down to `null` everywhere, which makes every
// `ADDRESS !== null` branch `never` and fails the build the moment a real
// address is filled in. The cast keeps the union live.
export const ADDRESS = {
  street: "21851 Ball Avenue",
  city: "Euclid",
  region: "OH",
  postalCode: "44123",
  country: "US",
} as ContactAddress | null;

/**
 * Registered company name, exactly as supplied by the team.
 *
 * Published because the Terms bind users to arbitration, and binding people to
 * a contract with a company the site never names is the hardest thing to defend
 * about an anonymous data business. No "LLC"/"Inc" suffix is appended here: the
 * name is printed as given rather than decorated into a legal form nobody
 * confirmed.
 */
export const LEGAL_ENTITY: string = "LeadsMiner";

/**
 * Where the company is REGISTERED, e.g. "Delaware, United States".
 *
 * Deliberately still empty. The registered address below is in Ohio, but a
 * company's state of incorporation is frequently not the state it operates
 * from, so inferring "Ohio" from a mailing address would be a guess about a
 * legal fact printed next to the entity name.
 */
export const JURISDICTION: string = "";

/**
 * Founding date, ISO `YYYY-MM-DD`. The page shows the year; Organization schema
 * gets the exact date as `foundingDate`. Matches the domain registration date.
 */
export const FOUNDED: string = "2026-01-05";

/** Founding year for display; schema uses the full ISO date above. */
export const FOUNDED_YEAR: string = FOUNDED ? FOUNDED.slice(0, 4) : "";

/** True when the address is complete enough to publish. */
export const hasAddress = (): boolean =>
  ADDRESS !== null && Boolean(ADDRESS.street && ADDRESS.city && ADDRESS.country);
